// SAB-1172 / SAB-1173: ONE batched query per list, never one per row.
//
// The badge has to know, for every player in a list, whether that player has ACTIVE
// news. The naive shape is a query inside PlayerNewsIcon, which on a draft board of
// 200+ rows is 200+ round trips on every render. That is the failure mode this hook
// exists to prevent -- callers pass the whole list once and get a Set back.

import { useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { activeWindowStartIso, weekAnchorStartIso } from '@/lib/playerNewsActive';

type Sport = 'mlb' | 'nfl';

// The current REG week's first kickoff, fetched once per session. Module-scoped
// rather than per-hook so twenty lists on one page share a single lookup.
let slateAnchor: string | null | undefined;
let slateAnchorPromise: Promise<string | null> | null = null;

async function getSlateAnchor(): Promise<string | null> {
  if (slateAnchor !== undefined) return slateAnchor;
  if (slateAnchorPromise) return slateAnchorPromise;

  slateAnchorPromise = (async () => {
    try {
      // Anchor on nfl_games, NOT nfl_leagues.current_week — see playerNewsActive.ts.
      const { data: latest } = await supabase
        .from('nfl_games')
        .select('season_year, week')
        .eq('season_type', 'REG')
        .lte('scheduled', new Date().toISOString())
        .order('scheduled', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (!latest) return null;

      const { data: first } = await supabase
        .from('nfl_games')
        .select('scheduled')
        .eq('season_type', 'REG')
        .eq('season_year', latest.season_year)
        .eq('week', latest.week)
        .order('scheduled', { ascending: true })
        .limit(1)
        .maybeSingle();
      // SAB-1191: truncate to the kickoff DAY. THIS is the live week clause -- the
      // `.or()` below is what actually drives the badge, and isActiveNews has no
      // production caller. Fixing only that predicate would have changed nothing.
      return first?.scheduled ? weekAnchorStartIso(first.scheduled) : null;
    } catch {
      return null;
    }
  })();

  slateAnchor = await slateAnchorPromise;
  return slateAnchor;
}

// The active set per sport, with a short TTL. Bounded by how much news exists, not by
// how many players a list shows.
const ACTIVE_SET_TTL_MS = 60_000;
const activeSetCache = new Map<Sport, { at: number; keys: Set<string> }>();

/** Test seam — reset the module-level caches. */
export function __resetSlateAnchorCache() {
  slateAnchor = undefined;
  slateAnchorPromise = null;
  activeSetCache.clear();
}

async function fetchActiveSet(sport: Sport): Promise<Set<string>> {
  const hit = activeSetCache.get(sport);
  if (hit && Date.now() - hit.at < ACTIVE_SET_TTL_MS) return hit.keys;

  const since = activeWindowStartIso();
  let rows: Array<Record<string, any>> | null = null;

  if (sport === 'nfl') {
    const anchor = await getSlateAnchor();
    let q = supabase.from('player_news').select('nfl_player_id').eq('sport', 'nfl');
    q = anchor
      ? q.or(`published_at.gte.${since},published_at.gte.${anchor}`)
      : q.gte('published_at', since);
    const { data } = await q;
    rows = data;
  } else {
    // MLB: 7-day window only. No game-week clause exists, by design.
    const { data } = await supabase
      .from('player_news')
      .select('player_name')
      .eq('sport', 'mlb')
      .gte('published_at', since);
    rows = data;
  }

  const col = sport === 'nfl' ? 'nfl_player_id' : 'player_name';
  const keys = new Set<string>((rows ?? []).map(r => r[col]).filter(Boolean));
  activeSetCache.set(sport, { at: Date.now(), keys });
  return keys;
}

/**
 * INVERTED QUERY — we fetch the players WITH active news, not the news for these players.
 *
 * The obvious shape is `.in('nfl_player_id', keys)`. That breaks on the draft board,
 * where the list is the whole available pool — ~2,500 players — and an `.in()` of 2,500
 * uuids is a PostgREST URL far past any sane length, issued on every filter change.
 *
 * The set of players with ACTIVE news is small and, crucially, bounded by the news
 * itself rather than by the size of the list: 99 rows across the whole NFL today, ~88
 * for MLB. So we ask for that set once, cache it, and intersect in memory. One query
 * per sport per page regardless of whether the caller is showing 12 rostered players or
 * 2,500 draftable ones.
 *
 * @param sport 'mlb' | 'nfl'
 * @param keys  MLB: player_name (the MLB read path is name-keyed). NFL: nfl_player_id.
 * @returns the subset of `keys` that have at least one ACTIVE news item.
 */
export function useActiveNewsPlayers(sport: Sport, keys: string[]): Set<string> {
  const [active, setActive] = useState<Set<string>>(() => new Set());

  // Sort + join so a re-render with the same players in a different order does not
  // refetch. This is what makes the hook safe to call from a list that re-renders on
  // every keystroke of a search box.
  const keyFingerprint = useMemo(
    () => [...new Set(keys.filter(Boolean))].sort().join('|'),
    [keys],
  );
  const latest = useRef(0);

  useEffect(() => {
    if (!keyFingerprint) { setActive(new Set()); return; }
    // The supabase client is stubbed as {} in some component tests (see
    // DraftBoard.mlbUnchanged.test.tsx). Degrade to "no active news" rather than
    // throwing, so the badge simply does not render and those tests stay honest.
    if (typeof (supabase as any)?.from !== 'function') { setActive(new Set()); return; }

    const ids = keyFingerprint.split('|');
    const token = ++latest.current;
    let cancelled = false;

    (async () => {
      try {
        const all = await fetchActiveSet(sport);
        if (cancelled || token !== latest.current) return;
        // Intersect in memory — the query above is independent of this list's size.
        setActive(new Set(ids.filter(id => all.has(id))));
      } catch {
        if (!cancelled && token === latest.current) setActive(new Set());
      }
    })();

    return () => { cancelled = true; };
  }, [sport, keyFingerprint]);

  return active;
}
